import { Box, ButtonBase, Stack, Typography, useTheme } from "@mui/material";


import Service from "../api/Service";
import { useEffect, useState } from "react";
import ToggleButton from "./ToggleButton";
import FormattedPrice from "./FormattedPrice";
import FormattedTime from "./FormattedTime";
import useLocalizedDescription from "../hooks/useLocalizedDescription";
import { FONTS, CARD_STYLES } from "../constants/styles";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DragIndicatorRounded from "@mui/icons-material/DragIndicatorRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";

const ServiceView = (props: {
    service: Service;
    editMode: boolean;
    onChange: (val: Service) => void;
    onSelect: (val: Service) => void;
    onMoveUp: () => void;
    onMoveDown: () => void;
    index: number;
    total: number;
}) => {
    const theme = useTheme();
    const {
        service,
        editMode,
        onChange,
        onSelect,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onMoveUp,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onMoveDown,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        index,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        total,
    } = props;

    const serviceName = useLocalizedDescription(
        service.name,
        service.nameOriginal
    );

    const [show, setShow] = useState(service.enabled);
    const toggleShow = (val: boolean) => {
        setShow(val);
        service.enabled = val;
        onChange(service);
    };

    useEffect(() => {
        setShow(service.enabled);
    }, [service.enabled]);

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: "svc:" + service.key, disabled: !editMode });

    const sortableStyle = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    if (editMode)
        return (
            <Stack
                ref={setNodeRef}
                style={sortableStyle}
                {...attributes}
                key={service.name}
                direction="row"
                spacing={1.5}
                alignItems="center"
                py={1}
                px={2}
                pl={3}
            >
                <Box
                    {...listeners}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        cursor: "grab",
                        touchAction: "none",
                        color: "text.secondary",
                        flexShrink: 0,
                    }}
                >
                    <DragIndicatorRounded sx={{ fontSize: 18 }} />
                </Box>
                <Stack spacing={0.5} flex={1} alignItems="start" minWidth={0}>
                    <Typography
                        fontWeight={500}
                        fontSize="14px"
                        lineHeight="14.56px"
                        minWidth={0}
                        sx={{
                            overflowWrap: "break-word",
                            wordBreak: "break-word",
                        }}
                    >
                        {serviceName}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <FormattedPrice
                            price={service.price}
                            currency={service.currency}
                        />
                        <FormattedTime time={service.duration} size="small" />
                    </Stack>
                </Stack>
                <ToggleButton value={show} onClick={toggleShow} />
            </Stack>
        );

    return (
        <ButtonBase
            sx={{
                padding: 0,
                margin: 0,
                borderRadius: "16px",
                border: "none",
                textTransform: "none",
                textAlign: "start",
                width: "100%",
                "&:active .service-card": {
                    transform: "scale(0.98)",
                },
            }}
            onClick={() => onSelect(service)}
        >
            <Stack
                className="service-card"
                key={service.name}
                direction="row"
                spacing={1.5}
                alignItems="center"
                width="100%"
                sx={{
                    ...CARD_STYLES,
                    p: 1.5,
                    transition: "transform 0.15s ease",
                }}
            >
                <Box
                    sx={{
                        width: 64,
                        minWidth: 64,
                        height: 64,
                        minHeight: 64,
                        borderRadius: "14px",
                        bgcolor: theme.palette.primary.main,
                        backgroundImage: `url(${service.image})`,
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                        backgroundSize: "cover",
                        flex: "0 0 auto",
                    }}
                />
                <Stack spacing={0.75} justifyContent="center" flex={1} minWidth={0}>
                    <Typography
                        fontWeight={600}
                        fontSize="16px"
                        lineHeight="20px"
                        fontFamily={FONTS.display}
                        minWidth={0}
                        sx={{
                            overflowWrap: "break-word",
                            wordBreak: "break-word",
                        }}
                    >
                        {serviceName}
                    </Typography>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                    >
                        <FormattedPrice
                            price={service.price}
                            currency={service.currency}
                        />
                        <FormattedTime time={service.duration} size="small" />
                    </Stack>
                </Stack>
                <ChevronRightRounded
                    sx={{
                        fontSize: 20,
                        color: "text.secondary",
                        opacity: 0.4,
                        flexShrink: 0,
                    }}
                />
            </Stack>
        </ButtonBase>
    );
};

export default ServiceView;
